import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getCurrentAuthUser } from '../lib/auth';
import { api } from '../lib/apiClient';
import { useAuthStore } from '../store/authStore';
import type { User } from '../types/user';

export function useAuth() {
  const { user, loading, setUser, setLoading, setNeedsDisplayName } = useAuthStore();

  useEffect(() => {
    async function init() {
      try {
        const cognitoUser = await getCurrentAuthUser();
        if (!cognitoUser) {
          setUser(null);
          return;
        }

        try {
          const profile = await api.get<User>('/users/me');
          if (!profile) {
            // First login — ask user to pick a game name
            setNeedsDisplayName(true);
          } else {
            setUser(profile);
          }
        } catch {
          // API is unavailable but the user IS authenticated — keep them signed in
          // with a minimal profile so they're not stuck in a sign-in loop
          setUser({
            userId: cognitoUser.userId,
            email: cognitoUser.signInDetails?.loginId ?? '',
            displayName: cognitoUser.signInDetails?.loginId?.split('@')[0] ?? cognitoUser.userId,
            provider: 'google',
            locale: 'ru',
          });
        }
      } catch {
        // getCurrentAuthUser threw — no active session
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    init();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        init();
      } else if (payload.event === 'signedOut') {
        setUser(null);
      }
    });

    return unsubscribe;
  }, [setUser, setLoading]);

  return { user, loading };
}
